const { getOffsetTop } = window.BAO.Utils

class HighlightsScroll extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    /* eslint-disable-next-line no-undef */
    if (Shopify.designMode) return

    window.setTimeout(() => {
      this.updateEl()
    }, 500)

    this.activeIndex = '1'

    this.isTicking = false
    this.lastKnownY = window.scrollY

    this.requestTick = this.requestTick.bind(this)
    this.updateEl = this.updateEl.bind(this)

    this.cachedItemEls = null
    this.cachedMediaEls = null
    this.cachedScrollPositions = null
    this.isVisible = false

    this.setupIntersectionObserver()
  }

  setupIntersectionObserver () {
    // eslint-disable-next-line compat/compat
    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.isVisible = entry.isIntersecting
        if (!entry.isIntersecting) {
          this.isTicking = false
        } else if (entry.isIntersecting) {
          this.requestTick()
        }
      },
      { rootMargin: '200px' },
    )
    this.observer.observe(this)
  }

  setupListeners () {
    super.setupListeners()

    this.onScroll = this.onScroll.bind(this)
    this.debouncedOnResize = window.BAO.Utils.debounce(() => {
      this.cachedScrollPositions = null
    }, 200)

    window.addEventListener('scroll', this.onScroll, { passive: true })
    window.addEventListener('resize', this.debouncedOnResize)
  }

  onScroll () {
    if (!this.isVisible) return

    this.lastKnownY = window.scrollY

    this.requestTick()
  }

  requestTick () {
    if (!this.isTicking) requestAnimationFrame(this.updateEl)

    this.isTicking = true
  }

  updateEl () {
    this.isTicking = false

    this.updateItems()
  }

  updateItems () {
    if (!this.cachedScrollPositions) {
      const containerOffsetTop = getOffsetTop(this.els.items.element)
      this.cachedScrollPositions = {
        start: containerOffsetTop - window.innerHeight / 2,
        end:
          containerOffsetTop -
          window.innerHeight / 2 +
          this.els.items.element.offsetHeight,
      }
    }
    const elementStartPoint = this.cachedScrollPositions.start
    const elementEndPoint = this.cachedScrollPositions.end

    if (this.lastKnownY <= elementStartPoint) {
      this.setActiveItem('1')

      return
    }

    if (this.lastKnownY > elementEndPoint) {
      this.setActiveItem(this.els?.item?.elements.length.toString())

      return
    }

    const totalScrollAmount = elementEndPoint - elementStartPoint

    const scrollYAsAPercentageOfJourney =
      (this.lastKnownY - elementStartPoint) / totalScrollAmount

    if (!this.cachedItemEls) {
      this.cachedItemEls = this.querySelectorAll(
        '[data-highlights-scroll-el="item"]',
      )
    }
    const itemEls = this.cachedItemEls

    itemEls.forEach((itemEl, index) => {
      const minimumPercent = index / this.els?.item?.elements.length
      const maximumPercent = (index + 1) / this.els?.item?.elements.length
      const isWithinRange =
        scrollYAsAPercentageOfJourney >= minimumPercent &&
        scrollYAsAPercentageOfJourney < maximumPercent

      if (isWithinRange) {
        this.setActiveItem(itemEl.dataset.index)
      }
    })
  }

  setActiveItem (newIndex) {
    if (this.activeIndex === newIndex) return

    this.activeIndex = newIndex

    if (!this.cachedItemEls) {
      this.cachedItemEls = this.querySelectorAll(
        '[data-highlights-scroll-el="item"]',
      )
    }
    if (!this.cachedMediaEls) {
      this.cachedMediaEls = this.querySelectorAll(
        '[data-highlights-scroll-el="media"]',
      )
    }
    const itemEls = this.cachedItemEls
    const mediaEls = this.cachedMediaEls

    itemEls.forEach((itemEl) => {
      itemEl.classList.toggle(
        'sec-Highlights_Item-active',
        itemEl.dataset.index === this.activeIndex,
      )

      if (itemEl.dataset.index === this.activeIndex) {
        if (itemEl.dataset.content) {
          const newClass = `sec-Highlights-${itemEl.dataset.content}`
          this.className = `${this.className.replace(/sec-Highlights-(light|dark)/g, '').trim()} ${newClass}`
          this.style.setProperty('--Overlay', itemEl.dataset.overlay)
        }
      }
    })

    mediaEls.forEach((mediaEl) => {
      mediaEl.setAttribute(
        'aria-hidden',
        mediaEl.dataset.index !== this.activeIndex,
      )
    })
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('resize', this.debouncedOnResize)

    this.cachedItemEls = null
    this.cachedMediaEls = null
    this.cachedScrollPositions = null
  }
}

if (!customElements.get('highlights-scroll')) {
  customElements.define('highlights-scroll', HighlightsScroll)
}
